﻿// ── src/js/Defender.js ──
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GroundedSkybox } from 'three/examples/jsm/objects/GroundedSkybox.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import { gsap } from 'gsap';

const params = { height: 15, radius: 100 };
let camera, scene, renderer, controls;
let bodyMesh, stephnie;
let wheelFR, wheelFL, wheelRR, wheelRL;
let isInterior = false, isWheelsSpinning = false, spinStartTime = 0;
const spinDuration = 2000, baseSpeed = 0.15;

const defaultCameraPos = new THREE.Vector3(-20, 7, 20);
const defaultTargetPos = new THREE.Vector3(0, 2, 0);
const interiorCameraPos = new THREE.Vector3(0.8, 7, -1.5);
const interiorTargetPos = new THREE.Vector3(-50, -4, 25.6);

async function init() {
    camera = new THREE.PerspectiveCamera(40, window.innerWidth / window.innerHeight, 1, 1000);
    camera.position.copy(defaultCameraPos);
    scene = new THREE.Scene();

    renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    document.body.appendChild(renderer.domElement);

    controls = new OrbitControls(camera, renderer.domElement);
    controls.target.copy(defaultTargetPos);
    controls.maxPolarAngle = Math.PI / 2;
    controls.maxDistance = 80;
    controls.minDistance = 20;
    controls.enablePan = false;
    controls.enableZoom = true;
    controls.zoomSpeed = 1;
    controls.addEventListener('change', render);
    controls.update();
    window.addEventListener('resize', onWindowResize);

    const hdr = await new RGBELoader().loadAsync('../assets/minedump_flats_4k.hdr');
    hdr.mapping = THREE.EquirectangularReflectionMapping;
    scene.environment = hdr;
    const sky = new GroundedSkybox(hdr, params.height, params.radius);
    sky.position.y = params.height - 0.01;
    scene.add(sky);

    const draco = new DRACOLoader().setDecoderPath('jsm/libs/draco/gltf/');
    const loader = new GLTFLoader().setDRACOLoader(draco);

    loader.load('../assets/DEFENDER_URBAN.glb', gltf => {
        gltf.scene.traverse(c => {
            if (c.isMesh) {
                c.castShadow = c.receiveShadow = true;
                c.material.envMap = scene.environment;
                if ('metalness' in c.material) {
                    c.material.metalness = 0.9; c.material.roughness = 0;
                }
                if (c.name === 'body_roof') bodyMesh = c;
                if (['wheel_spare_cover', 'wheel_spare_cover_part'].includes(c.name)) stephnie = c;
            }
        });
        wheelFR = gltf.scene.getObjectByName('wheelsFrontRight');
        wheelFL = gltf.scene.getObjectByName('wheelsFrontLeft');
        wheelRR = gltf.scene.getObjectByName('wheelsRearRight');
        wheelRL = gltf.scene.getObjectByName('wheelsRearLeft');

        gltf.scene.scale.set(5, 5, 5);
        gltf.scene.rotation.y = 5.1;
        scene.add(gltf.scene);
        render();
        setTimeout(buildUI, 0);
    });

    animate();
}

function buildUI() {
    const colors = {
        '#ffffff': { metalness: 1, roughness: 0.15 },
        '#333333': { metalness: 0.8, roughness: 0.3 },
        '#416c84': { metalness: 0.4, roughness: 0.2 },
        '#ff0000': { metalness: 1, roughness: 0.3 },
        '#808080': { metalness: 0.9, roughness: 0.2 },
        '#2a3439': { metalness: 1, roughness: 0.15 },
        '#b87333': { metalness: 0.8, roughness: 0.3 },
        '#ff8c00': { metalness: 1, roughness: 0.1 }
    };

    const navBar = document.createElement('div');
    Object.assign(navBar.style, {
        position: 'absolute', top: '10px', left: '50%', transform: 'translateX(-50%)',
        width: '70%', padding: '10px 20px', borderRadius: '8px',
        backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(5px)',
        display: 'flex', justifyContent: 'space-between', color: '#fff', zIndex: 999
    });
    document.body.appendChild(navBar);

    const brand = document.createElement('div'); brand.innerText = 'Defender';
    navBar.appendChild(brand);

    const container = document.createElement('div');
    container.style.display = 'flex'; container.style.gap = '20px';
    navBar.appendChild(container);

    let interiorBtn, infoBtn;
    ['COLOR', 'WHEELS', 'INTERIOR', 'INFO'].forEach(item => {
        const btn = document.createElement('div');
        btn.innerText = item; btn.style.cursor = 'pointer'; btn.style.fontSize = '0.9rem';
        if (item === 'INTERIOR') interiorBtn = btn;
        if (item === 'INFO') infoBtn = btn;
        container.appendChild(btn);

        btn.onclick = () => {
            document.querySelector('#colorPanel')?.remove();
            document.querySelector('#infoPanel')?.remove();

            switch (item) {
                case 'COLOR':
                    const panel = document.createElement('div'); panel.id = 'colorPanel';
                    Object.assign(panel.style, {
                        position: 'absolute', top: '70px', left: '50%',
                        transform: 'translateX(-50%)', padding: '10px',
                        borderRadius: '8px', backgroundColor: 'rgba(0,0,0,0.5)',
                        backdropFilter: 'blur(10px)', display: 'flex', gap: '8px',
                        color: '#fff', zIndex: 998
                    });
                    Object.entries(colors).forEach(([col, sett]) => {
                        const sw = document.createElement('div');
                        Object.assign(sw.style, {
                            width: '30px', height: '30px', borderRadius: '4px',
                            backgroundColor: col, border: '1px solid #ccc', cursor: 'pointer'
                        });
                        sw.onclick = () => {
                            if (bodyMesh) {
                                bodyMesh.material.color.set(col);
                                bodyMesh.material.metalness = sett.metalness;
                                bodyMesh.material.roughness = sett.roughness;
                            }
                            if (stephnie) {
                                stephnie.material.color.set(col);
                                stephnie.material.metalness = sett.metalness;
                                stephnie.material.roughness = sett.roughness;
                            }
                            render();
                        };
                        panel.appendChild(sw);
                    });
                    document.body.appendChild(panel);
                    moveTo(defaultCameraPos, defaultTargetPos, () => {
                        controls.enableRotate = true; controls.enableZoom = true; controls.enablePan = false;
                    });
                    break;

                case 'WHEELS':
                    if (wheelFR && wheelFL && wheelRR && wheelRL) {
                        isWheelsSpinning = true; spinStartTime = performance.now();
                    }
                    break;

                case 'INTERIOR':
                    if (!isInterior) {
                        moveTo(interiorCameraPos, interiorTargetPos, () => {
                            controls.enableRotate = false; controls.enableZoom = false; controls.enablePan = false;
                            interiorBtn.innerText = 'EXTERIOR'; isInterior = true;
                        });
                    } else {
                        moveTo(defaultCameraPos, defaultTargetPos, () => {
                            controls.enableRotate = true; controls.enableZoom = true; controls.enablePan = false;
                            interiorBtn.innerText = 'INTERIOR'; isInterior = false;
                        });
                    }
                    break;

                case 'INFO':
                    const info = document.createElement('div'); info.id = 'infoPanel';
                    Object.assign(info.style, {
                        position: 'absolute', padding: '15px', borderRadius: '8px',
                        backgroundColor: 'rgba(100,100,100,0.6)', color: '#fff', zIndex: 1001,
                        transformOrigin: 'top center', opacity: '0', transform: 'scale(0.8)',
                        transition: 'opacity 0.3s ease, transform 0.3s ease'
                    });
                    info.innerHTML = `
            <h3>Land Rover Defender (2025)</h3>
            <strong>Engines:</strong>
            <ul>
              <li>2.0L Petrol – 296 hp</li>
              <li>3.0L Diesel – 296 hp</li>
              <li>5.0L V8 Petrol – 426 hp, 610 Nm</li>
            </ul>
            <strong>Transmission:</strong> 8-speed automatic<br/>
            <strong>Drive Type:</strong> AWD with Terrain Response<br/>
            <strong>Seating:</strong> Up to 8-seater<br/>
            <strong>Features:</strong> 11.4" touchscreen, 360° camera, air suspension, heated/cooled seats, panoramic sunroof<br/>
            <strong>Price:</strong> Starts at ₹1.39 crore<br/>
            <div style="text-align:right; margin-top:10px;">
              <button id="closeInfo" style="cursor:pointer;">Close</button>
            </div>`;
                    const r = infoBtn.getBoundingClientRect();
                    info.style.top = `${r.bottom + 8}px`;
                    info.style.left = `${r.left + r.width / 2}px`;
                    info.style.transform = 'translateX(-50%) scale(0.8)';
                    document.body.appendChild(info);
                    requestAnimationFrame(() => {
                        info.style.opacity = '1';
                        info.style.transform = 'translateX(-50%) scale(1)';
                    });
                    document.getElementById('closeInfo').onclick = () => {
                        info.style.opacity = '0';
                        info.style.transform = 'translateX(-50%) scale(0.8)';
                        setTimeout(() => info.remove(), 300);
                    };
                    break;
            }
        };
    });
}

function animate() {
    requestAnimationFrame(animate);
    if (isWheelsSpinning) {
        const elapsed = performance.now() - spinStartTime;
        let speed = elapsed < 1500 ? baseSpeed
            : elapsed < spinDuration ? baseSpeed * ((spinDuration - elapsed) / (spinDuration - 1500))
                : (isWheelsSpinning = false, 0);
        [wheelFR, wheelFL, wheelRR, wheelRL].forEach(w => w && (w.rotation.x += speed));
        render();
    }
}

function moveTo(camPos, tgtPos, cb) {
    gsap.to(camera.position, {
        x: camPos.x, y: camPos.y, z: camPos.z,
        duration: 1, ease: 'power2.inOut',
        onUpdate: () => { controls.update(); render(); },
        onComplete: cb
    });
    gsap.to(controls.target, {
        x: tgtPos.x, y: tgtPos.y, z: tgtPos.z,
        duration: 1, ease: 'power2.inOut',
        onUpdate: () => { controls.update(); render(); }
    });
}

function onWindowResize() {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
    render();
}

function render() {
    renderer.render(scene, camera);
}

export async function initDefender(container = document.body) {
    try { await init(); }
    catch (err) { console.error('Defender error:', err); }
}
